import React from 'react';

import { Flex, HStack, Icon, Progress, Stack, Text } from '@chakra-ui/react';

import STATUS_VARIANTS from './shared';
import type { SectionStatus } from './types';

type SectionStatusCardProps = SectionStatus & {
  isActive: boolean;
  onSelect: (sectionId: SectionStatus['id']) => void;
};

const clampPercentage = (value: number): number =>
  Math.min(Math.max(value, 0), 100);

const getProgressPercentage = (
  progress: SectionStatus['progress'] | null,
): number => {
  if (!progress) return 0;

  const percent = Number(progress.progress_percent);
  if (Number.isFinite(percent)) {
    return clampPercentage(percent);
  }

  const current = Number(progress.progress);
  const total = Number(progress.max_steps);
  if (Number.isFinite(current) && Number.isFinite(total) && total > 0) {
    return clampPercentage((current / total) * 100);
  }

  return 0;
};

const SectionStatusCard: React.FC<SectionStatusCardProps> = ({
  id,
  title,
  statusLabel,
  variant,
  progress,
  isActive,
  onSelect,
}) => {
  const variantStyle = STATUS_VARIANTS[variant] ?? STATUS_VARIANTS.waiting;
  const { bg, borderColor, color, icon: StatusIcon, iconColor } = variantStyle;
  const percentage = getProgressPercentage(progress);

  return (
    <Flex
      as="button"
      type="button"
      onClick={() => onSelect(id)}
      direction="column"
      borderWidth="2px"
      borderColor={isActive ? 'brand.100' : borderColor}
      borderRadius="md"
      bg={bg}
      color={color}
      px={8}
      py={6}
      boxShadow={isActive ? 'md' : 'sm'}
      gap={4}
      cursor="pointer"
      transition="all 0.2s ease"
      _hover={{ boxShadow: 'md' }}
      textAlign="left"
    >
      <Text fontSize="xl" fontWeight="bold">
        {title}
      </Text>
      <HStack spacing={2} align="center">
        <Icon as={StatusIcon} boxSize={5} color={iconColor} />
        <Text fontSize="md" fontWeight="medium">
          {statusLabel}
        </Text>
      </HStack>

      <Stack spacing={2}>
        <Progress
          value={progress ? percentage : 0}
          borderRadius="full"
          isAnimated={progress?.status === 'running'}
          hasStripe={progress?.status === 'running'}
          isIndeterminate={!progress}
          colorScheme={progress ? 'blue' : 'gray'}
          opacity={progress ? 1 : 0.5}
          height="3px"
        />
      </Stack>
    </Flex>
  );
};

export default SectionStatusCard;
